import { computed, type ComputedRef } from 'vue';
import type { SelectMixedOption } from 'naive-ui/es/select/src/interface';
import type { SigningProvider } from '@/api';
import { signingProviderMap } from '@/components/design/statusMaps';
import { useAuthStore } from '@/stores/auth';
import { useOrganizationStore } from '@/stores/organizationStore';

/**
 * The signing providers this user can send through, in the backend's order:
 * what the active organisation can use (its signing feature, its connected
 * Yousign integration) — and nothing for a user without the signing
 * permission. `defaultProvider` is the organisation's pick for one-click
 * sends, null when it has none or the provider is no longer available.
 */
export function useSigningProviderOptions(): {
  providerOptions: ComputedRef<SelectMixedOption[]>;
  defaultProvider: ComputedRef<SigningProvider | null>;
  /** How many days a new signing request runs before it expires (organisation setting). */
  expirationDays: ComputedRef<number>;
} {
  const authStore = useAuthStore();
  const orgStore = useOrganizationStore();

  const available = computed<SigningProvider[]>(() =>
    authStore.userPermissions.can_use_signing
      ? (orgStore.activeOrganization?.available_signing_providers ?? [])
      : []
  );

  const providerOptions = computed<SelectMixedOption[]>(() =>
    available.value.map(provider => ({ label: signingProviderMap[provider], value: provider }))
  );

  const defaultProvider = computed<SigningProvider | null>(() => {
    const provider = orgStore.activeOrganization?.default_signing_provider ?? null;
    return provider && available.value.includes(provider) ? provider : null;
  });

  const expirationDays = computed(() => orgStore.activeOrganization?.signing_expiration_days ?? 30);

  return { providerOptions, defaultProvider, expirationDays };
}
